import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { CircleAlert, TriangleAlert } from 'lucide-react';
import {
  format, parseISO, startOfYear, endOfYear, endOfMonth,
  eachMonthOfInterval, eachDayOfInterval, getDay,
  isWeekend, getMonth, getDayOfYear, getDaysInYear,
} from 'date-fns';
import { PageLoading, PageError } from '../components/common/PageState';
import {
  BarList, ChartCard, EmptyState, FilterChip, KpiGrid, KpiTile, LegendItem, Meter, PageHeader,
  PersonCell, SearchField, SegmentedControl, SpecGrid, StatusPill, TableCard, TableEmptyRow, Toolbar,
  useDelayedFlag,
} from '../components/ui';
import type { BarListItem, KpiTileProps } from '../components/ui';
import { focusRing, mix, sbg } from '../theme/sbgTokens';
import {
  MISSING, formatCount, formatDateRange, formatDateTime, formatDays, formatDelta, formatNumber, formatPercent,
  formatRelative, formatTime,
} from '../utils/format';
import { useGetEmployeesQuery } from '../store/slices/employeeSlice';
import { useGetDepartmentsQuery } from '../store/slices/departmentSlice';
import { useGetFilteredTimeOffEntriesQuery, TimeOffType, TimeOffStatus } from '../store/slices/timeOffSlice';

// ── Helpers ──
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const DAY_NAMES_LONG: Record<string, string> = {
  Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday',
};

const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);

// Fraction of an entry's business days that have elapsed (inclusive of today).
// Entry hours are allocated per business day on the backend, so prorating by
// business days mirrors how the hours were originally computed.
const elapsedBusinessDayFraction = (startYmd: string, endYmd: string, todayYmd: string) => {
  const days = eachDayOfInterval({ start: parseISO(startYmd), end: parseISO(endYmd) })
    .filter((d) => !isWeekend(d));
  if (days.length === 0) return 1;
  const elapsed = days.filter((d) => format(d, 'yyyy-MM-dd') <= todayYmd).length;
  return elapsed / days.length;
};

/** "1,284 h" — hours with their unit, for dense rows and tables. */
const hoursLabel = (value: number) => `${formatNumber(value)} h`;

type Segment = 'all' | 'sales' | 'non-sales';

const SEGMENT_SCOPE: Record<Segment, string> = {
  all: 'All teams',
  sales: 'Sales teams',
  'non-sales': 'Non-sales teams',
};

// Categorical series in fixed order by entity (book §13.3): PTO is always
// slot 1, Sick always slot 2, through every filter.
const PTO_COLOR = sbg.chart.series[0];
const SICK_COLOR = sbg.chart.series[1];

/** Coverage below these lines is a true "low" state (kept from the original thresholds). */
const COVERAGE_WARNING = 90;
const COVERAGE_DANGER = 85;

/** A team needs this many people before its per-person figure headlines the team chart. */
const MIN_TEAM_SIZE = 3;
/** "Above average" marks a team only this far over the average, and only when such teams are a minority. */
const ABOVE_AVERAGE_MARGIN = 1.2;
const TEAM_ROWS = 8;

/** After this long on the skeleton, say what is still loading and offer a reload. */
const SLOW_LOAD_MS = 10000;

// ── Chart primitives (local; the kit has no time-series chart yet) ──

const useElementWidth = <T extends HTMLElement>(fallback = 640) => {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = () => setWidth(element.clientWidth || fallback);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [fallback]);
  return [ref, width] as const;
};

/** A rounded axis ceiling on a 1-2-5 step so gridlines land on whole numbers. */
const niceScale = (max: number, steps = 4) => {
  if (max <= 0) return { top: steps, step: 1 };
  const raw = max / steps;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const residual = raw / magnitude;
  const nice = residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 5 ? 5 : 10;
  const step = Math.max(1, nice * magnitude);
  return { top: Math.ceil(max / step) * step, step };
};

interface LineSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

interface ChartPoint {
  title: string;
  rows: { key: string; label: string; value: string; color?: string }[];
}

const CHART_HEIGHT = 240;
const CHART_MARGIN = { top: 12, right: 44, bottom: 28, left: 52 };
const svgText: React.CSSProperties = { font: 'var(--sbg-type-caption)', fontVariantNumeric: 'tabular-nums' };

/**
 * Monthly line chart (book §13.4): 2px lines, horizontal hairline grid,
 * caption tick labels, direct labels at the line ends, and a crosshair
 * tooltip on hover or arrow keys. Points after `lastCompleteIndex` (the
 * month in progress and months booked ahead) draw dashed.
 */
const MonthlyLineChart: React.FC<{
  labels: string[];
  series: LineSeries[];
  lastCompleteIndex: number;
  /** Where keyboard focus lands first: the month in progress. */
  focusIndex: number;
  pointAt: (index: number) => ChartPoint;
  'aria-label': string;
}> = ({ labels, series, lastCompleteIndex, focusIndex, pointAt, ...rest }) => {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  // Only keyboard moves are announced; hovering would flood the live region.
  const [announced, setAnnounced] = useState<number | null>(null);
  const n = labels.length;
  const plotW = Math.max(1, width - CHART_MARGIN.left - CHART_MARGIN.right);
  const plotH = CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom;
  const stepX = n > 1 ? plotW / (n - 1) : 0;
  const x = (i: number) => CHART_MARGIN.left + (n > 1 ? i * stepX : plotW / 2);
  const { top, step } = niceScale(Math.max(0, ...series.flatMap((s) => s.values)));
  const y = (value: number) => CHART_MARGIN.top + plotH - (value / top) * plotH;
  const ticks: number[] = [];
  for (let value = 0; value <= top; value += step) ticks.push(value);
  const labelEvery = Math.max(1, Math.ceil(36 / Math.max(stepX, 1)));
  const solidEnd = Math.min(lastCompleteIndex, n - 1);
  const startIndex = Math.max(0, Math.min(focusIndex, n - 1));

  const pathFor = (values: number[], from: number, to: number) =>
    values
      .slice(from, to + 1)
      .map((value, k) => `${k === 0 ? 'M' : 'L'}${x(from + k).toFixed(1)},${y(value).toFixed(1)}`)
      .join(' ');

  // Direct labels sit past the last point, nudged apart when lines meet.
  const directLabels = series
    .map((s) => ({ key: s.key, label: s.label, y: y(s.values[n - 1] ?? 0) }))
    .sort((a, b) => a.y - b.y);
  for (let i = 1; i < directLabels.length; i += 1) {
    if (directLabels[i].y - directLabels[i - 1].y < 14) directLabels[i].y = directLabels[i - 1].y + 14;
  }

  const indexAt = (event: React.MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const mx = ((event.clientX - rect.left) * width) / Math.max(rect.width, 1);
    if (n <= 1) return 0;
    return Math.max(0, Math.min(n - 1, Math.round((mx - CHART_MARGIN.left) / stepX)));
  };

  const onKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
    if (event.key === 'Escape') {
      setActive(null);
      setAnnounced(null);
      return;
    }
    const start = active ?? startIndex;
    const next =
      event.key === 'ArrowRight' ? Math.min(n - 1, start + 1)
        : event.key === 'ArrowLeft' ? Math.max(0, start - 1)
          : event.key === 'Home' ? 0
            : event.key === 'End' ? n - 1
              : null;
    if (next === null) return;
    event.preventDefault();
    setActive(next);
    setAnnounced(next);
  };

  const point = active !== null ? pointAt(active) : null;
  const announcedPoint = announced !== null ? pointAt(announced) : null;
  // The tooltip sits beside the crosshair, on the roomier side, so it never
  // covers the points being inspected.
  const activeX = active !== null ? x(active) : 0;
  const tooltipSide = activeX <= width / 2 ? { left: activeX + 12 } : { right: Math.max(0, width - activeX + 12) };

  return (
    <Box ref={ref} sx={{ position: 'relative', minWidth: 0 }}>
      <Box
        component="svg"
        viewBox={`0 0 ${width} ${CHART_HEIGHT}`}
        role="img"
        aria-label={rest['aria-label']}
        tabIndex={0}
        onMouseMove={(event: React.MouseEvent<SVGSVGElement>) => setActive(indexAt(event))}
        onMouseLeave={() => setActive(null)}
        onFocus={() => {
          setActive(startIndex);
          setAnnounced(startIndex);
        }}
        onBlur={() => {
          setActive(null);
          setAnnounced(null);
        }}
        onKeyDown={onKeyDown}
        sx={{ display: 'block', width: '100%', height: CHART_HEIGHT, borderRadius: sbg.radius.md, '&:focus-visible': focusRing }}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={CHART_MARGIN.left}
              x2={width - CHART_MARGIN.right}
              y1={y(tick)}
              y2={y(tick)}
              stroke={tick === 0 ? sbg.chart.axis : sbg.chart.grid}
              strokeWidth={1}
              shapeRendering="crispEdges"
            />
            <text x={CHART_MARGIN.left - 8} y={y(tick)} textAnchor="end" dominantBaseline="middle" fill={sbg.chart.label} style={svgText}>
              {hoursLabel(tick)}
            </text>
          </g>
        ))}
        {labels.map((label, i) =>
          i % labelEvery === 0 ? (
            <text key={label} x={x(i)} y={CHART_HEIGHT - 8} textAnchor="middle" fill={sbg.chart.label} style={svgText}>
              {label}
            </text>
          ) : null,
        )}
        {active !== null && (
          <line
            x1={x(active)}
            x2={x(active)}
            y1={CHART_MARGIN.top}
            y2={CHART_MARGIN.top + plotH}
            stroke={sbg.chart.axis}
            strokeWidth={1}
            shapeRendering="crispEdges"
          />
        )}
        {series.map((s) => (
          <g key={s.key}>
            {solidEnd >= 1 && (
              <path d={pathFor(s.values, 0, solidEnd)} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            )}
            {solidEnd < n - 1 && (
              <path
                d={pathFor(s.values, Math.max(0, solidEnd), n - 1)}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
                strokeDasharray="4 4"
                strokeLinejoin="round"
              />
            )}
            {solidEnd >= 0 && active === null && <circle cx={x(solidEnd)} cy={y(s.values[solidEnd])} r={3} fill={s.color} />}
            {active !== null && (
              <circle cx={x(active)} cy={y(s.values[active])} r={4} fill={s.color} stroke={sbg.bg.surface} strokeWidth={2} />
            )}
          </g>
        ))}
        {directLabels.map((label) => (
          <text key={label.key} x={width - CHART_MARGIN.right + 8} y={label.y} dominantBaseline="middle" fill={sbg.text.secondary} style={svgText}>
            {label.label}
          </text>
        ))}
      </Box>
      {point && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: CHART_MARGIN.top,
            ...tooltipSide,
            pointerEvents: 'none',
            minWidth: 168,
            px: 1.5,
            py: 1,
            display: 'grid',
            gap: 0.5,
            bgcolor: sbg.bg.inverse,
            color: sbg.text.inverse,
            borderRadius: sbg.radius.md,
            boxShadow: sbg.shadow.md,
            zIndex: 1,
          }}
        >
          <Typography variant="labelSm" component="p">{point.title}</Typography>
          {point.rows.map((row) => (
            <Box key={row.key} sx={{ display: 'grid', gridTemplateColumns: '10px 1fr auto', alignItems: 'center', columnGap: 0.75 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: row.color ?? 'transparent' }} />
              <Typography variant="caption" component="span">{row.label}</Typography>
              <Typography variant="caption" component="span" sx={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      <span className="sbg-sr-only" aria-live="polite">
        {announcedPoint
          ? `${announcedPoint.title}: ${announcedPoint.rows.map((row) => `${row.label} ${row.value}`).join(', ')}`
          : ''}
      </span>
    </Box>
  );
};

/** A legend swatch for the dashed segment of the trend lines. */
const DashedLegendItem: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box aria-hidden="true" sx={{ width: 16, borderTop: `2px dashed ${sbg.text.secondary}` }} />
    <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
      {label}
    </Typography>
  </Box>
);

/** A legend entry for the coverage grid: the mark a month carries, then its word. */
const KeyItem: React.FC<{ mark: React.ReactNode; label: string }> = ({ mark, label }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box aria-hidden="true" sx={{ display: 'inline-flex' }}>{mark}</Box>
    <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
      {label}
    </Typography>
  </Box>
);

/**
 * Allotment pace bar: solid = approved hours already taken, hatched =
 * booked or pending on top, the vertical marker = share of the year elapsed.
 */
const PaceBar: React.FC<{
  usedPct: number;
  committedPct: number;
  elapsedPct: number;
  tone: 'brand' | 'warning' | 'danger';
  label: string;
}> = ({ usedPct, committedPct, elapsedPct, tone, label }) => {
  const fill = tone === 'danger' ? sbg.status.danger.icon : tone === 'warning' ? sbg.palette.warning[500] : sbg.action.primary;
  return (
    <Box role="img" aria-label={label} sx={{ position: 'relative', height: 8, minWidth: 120, borderRadius: sbg.radius.pill, bgcolor: sbg.bg.track }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: `${Math.min(committedPct, 100)}%`,
          borderRadius: 'inherit',
          background: `repeating-linear-gradient(-30deg, ${fill} 0 2px, ${mix(fill, 24)} 2px 5px)`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: `${Math.min(usedPct, 100)}%`,
          borderRadius: 'inherit',
          bgcolor: fill,
          transition: `width ${sbg.duration.slow} ${sbg.ease.enter}`,
        }}
      />
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: -3,
          bottom: -3,
          left: `${Math.min(elapsedPct, 100)}%`,
          width: 2,
          ml: '-1px',
          borderRadius: '1px',
          bgcolor: sbg.text.primary,
        }}
      />
    </Box>
  );
};

/** A section title that sits above a full-width table. */
const SectionHeading: React.FC<{ id: string; title: React.ReactNode; description?: React.ReactNode }> = ({
  id,
  title,
  description,
}) => (
  <Box sx={{ minWidth: 0, maxWidth: '76ch', mb: 1.5 }}>
    <Typography id={id} variant="h3" component="h2" sx={{ color: sbg.text.primary }}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.5 }}>
        {description}
      </Typography>
    )}
  </Box>
);

/**
 * "Updated 4m ago" for the page header (book §14.3): relative, ticking each
 * minute, with the exact time in ET on hover or focus.
 */
const UpdatedAgo: React.FC<{ at: number }> = ({ at }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);
  const relative = formatRelative(at, new Date(Math.max(now, at)));
  const text = /^(Just now|Yesterday)$/.test(relative) ? relative.toLowerCase() : relative;
  return (
    <Tooltip title={`Updated ${formatDateTime(at)}`} describeChild>
      <Box component="span" tabIndex={0} sx={{ borderRadius: sbg.radius.md, '&:focus-visible': focusRing }}>
        Updated {text}
      </Box>
    </Tooltip>
  );
};

/**
 * Over allotment always implies pacing over (projected ≥ committed), so the
 * pacing buckets are made exclusive to match the Status column: "Pacing over"
 * is everyone on pace to exceed who is not already over allotment.
 */
const isPacingOnly = (row: { overPace: boolean; overAllotment: boolean }) => row.overPace && !row.overAllotment;

const joinList = (items: string[]) =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

const Analytics: React.FC = () => {
  const {
    data: allEmployees = [], isLoading: employeesLoading, error: employeesError, refetch: reloadEmployees,
    fulfilledTimeStamp: employeesFetchedAt,
  } = useGetEmployeesQuery();
  const {
    data: departments = [], isLoading: departmentsLoading, error: departmentsError, refetch: reloadDepartments,
    fulfilledTimeStamp: departmentsFetchedAt,
  } = useGetDepartmentsQuery();
  const [yearFilter] = useState(new Date().getFullYear());
  // Scope the fetch to entries STARTING in the analyzed year (both bounds on
  // start_date): this page buckets by start date, and the table already holds
  // future-year bookings that would otherwise ride along.
  const {
    data: timeOffEntries = [], isLoading: entriesLoading, error: entriesError, refetch: reloadEntries,
    fulfilledTimeStamp: entriesFetchedAt,
  } = useGetFilteredTimeOffEntriesQuery({
    startDate: `${yearFilter}-01-01`,
    startBefore: `${yearFilter}-12-31`,
  });
  const anyLoading = employeesLoading || departmentsLoading || entriesLoading;
  const slowLoad = useDelayedFlag(anyLoading, SLOW_LOAD_MS);
  const [segmentFilter, setSegmentFilter] = useState<Segment>('all');
  // Presentational filters for the pacing table only.
  const [paceSearch, setPaceSearch] = useState('');
  const [paceFlags, setPaceFlags] = useState({ pacingOver: false, overAllotment: false });

  // Active employees only
  const employees = useMemo(
    () => allEmployees.filter((e) => !e.employmentStatus || e.employmentStatus === 'active'),
    [allEmployees]
  );

  // Department lookup for is_sales_team (based on "Team*" naming)
  const salesDeptIds = useMemo(() => {
    const set = new Set<string>();
    departments.forEach((d) => {
      if (d.name.startsWith('Team') && d.name !== 'Team IT') set.add(d._id);
    });
    return set;
  }, [departments]);

  const isSalesEmp = (emp: { department?: { _id?: string; id?: string } | null }) => {
    const departmentId = emp.department?._id || emp.department?.id;
    return departmentId ? salesDeptIds.has(departmentId) : false;
  };

  // Filtered employees by segment
  const filteredEmployees = useMemo(() => {
    if (segmentFilter === 'sales') return employees.filter(isSalesEmp);
    if (segmentFilter === 'non-sales') return employees.filter((e) => !isSalesEmp(e));
    return employees;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, segmentFilter, salesDeptIds]);

  const totalCount = filteredEmployees.length;
  const salesCount = employees.filter(isSalesEmp).length;
  const managersInView = filteredEmployees.filter((e) => e.role === 'manager' || e.role === 'Exec').length;

  // Year range
  const yearStart = startOfYear(new Date(yearFilter, 0, 1));
  const yearEnd = endOfYear(new Date(yearFilter, 0, 1));
  const today = new Date();

  // Filter entries to year + segment
  const filteredEntryIds = useMemo(() => new Set(filteredEmployees.map((e) => e._id)), [filteredEmployees]);

  const yearEntries = useMemo(() => {
    return timeOffEntries.filter((e) => {
      if (e.status === TimeOffStatus.REJECTED) return false;
      const start = parseISO(e.startDate);
      return start >= yearStart && start <= yearEnd && filteredEntryIds.has(e.employee._id);
    });
  }, [timeOffEntries, yearStart, yearEnd, filteredEntryIds]);

  // Per-person PTO, sick and WFH for the REST of the company (the other
  // group), the baseline a Sales or Non-sales view is compared against. A
  // baseline that contained the group itself would barely move. Same
  // inclusion rules as yearEntries.
  const restPerPerson = useMemo(() => {
    if (segmentFilter === 'all') return null;
    const rest = employees.filter((e) => !filteredEntryIds.has(e._id));
    if (rest.length === 0) return null;
    const ids = new Set(rest.map((e) => e._id));
    const from = startOfYear(new Date(yearFilter, 0, 1));
    const to = endOfYear(new Date(yearFilter, 0, 1));
    let pto = 0;
    let sick = 0;
    let wfh = 0;
    timeOffEntries.forEach((e) => {
      if (e.status === TimeOffStatus.REJECTED || !ids.has(e.employee._id)) return;
      const start = parseISO(e.startDate);
      if (start < from || start > to) return;
      if (e.type === TimeOffType.PTO) pto += e.hours;
      if (e.type === TimeOffType.SICK) sick += e.hours;
      if (e.type === TimeOffType.WFH) wfh += e.hours;
    });
    const n = rest.length;
    return {
      pto: Math.round(pto / n),
      sick: Math.round(sick / n),
      wfhDays: Math.round((wfh / 8 / n) * 10) / 10,
    };
  }, [segmentFilter, employees, filteredEntryIds, timeOffEntries, yearFilter]);

  // ── Monthly Absence Data ──
  const monthlyData = useMemo(() => {
    const months = eachMonthOfInterval({ start: yearStart, end: yearEnd });
    return months.map((monthStart) => {
      const monthEnd = endOfMonth(monthStart);
      const monthEntries = yearEntries.filter((e) => {
        const start = parseISO(e.startDate);
        const end = parseISO(e.endDate);
        return start <= monthEnd && end >= monthStart;
      });

      const ptoHours = monthEntries.filter((e) => e.type === TimeOffType.PTO).reduce((s, e) => s + e.hours, 0);
      const sickHours = monthEntries.filter((e) => e.type === TimeOffType.SICK).reduce((s, e) => s + e.hours, 0);
      const totalHours = ptoHours + sickHours;
      const uniqueOut = new Set(
        monthEntries.filter((e) => e.type !== TimeOffType.WFH && e.hours > 0).map((e) => e.employee._id)
      ).size;

      // Approximate working days in month (weekdays)
      const allDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
      const workDays = allDays.filter((d) => !isWeekend(d)).length;
      const possibleHours = totalCount * workDays * 8;
      const coveragePct = possibleHours > 0 ? Math.round(((possibleHours - totalHours) / possibleHours) * 100) : 100;

      return {
        month: format(monthStart, 'MMM'),
        monthLong: format(monthStart, 'MMMM'),
        monthIdx: getMonth(monthStart),
        ptoHours,
        sickHours,
        totalHours,
        uniqueOut,
        coveragePct,
      };
    });
  }, [yearEntries, yearStart, yearEnd, totalCount]);

  // ── Day-of-Week Patterns ──
  const dowData = useMemo(() => {
    const counts = [0, 0, 0, 0, 0]; // Mon-Fri
    yearEntries.forEach((e) => {
      if (e.hours === 0) return;
      const start = parseISO(e.startDate);
      const end = parseISO(e.endDate);
      const days = eachDayOfInterval({ start, end });
      days.forEach((d) => {
        const dow = getDay(d);
        if (dow >= 1 && dow <= 5) counts[dow - 1]++;
      });
    });
    const max = Math.max(...counts, 1);
    return DAY_NAMES.map((name, idx) => ({ name, count: counts[idx], pct: pct(counts[idx], max) }));
  }, [yearEntries]);

  // ── Team Coverage Ranking ──
  const teamCoverage = useMemo(() => {
    const teamMap = new Map<string, { name: string; total: number; absenceHours: number; ptoHours: number; sickHours: number }>();

    filteredEmployees.forEach((emp) => {
      const teamName = emp.teamName || emp.department?.name || 'Unassigned';
      if (!teamMap.has(teamName)) teamMap.set(teamName, { name: teamName, total: 0, absenceHours: 0, ptoHours: 0, sickHours: 0 });
      teamMap.get(teamName)!.total++;
    });

    yearEntries.forEach((e) => {
      if (e.type === TimeOffType.WFH) return;
      const emp = filteredEmployees.find((em) => em._id === e.employee._id);
      const teamName = emp?.teamName || emp?.department?.name || e.employee.department?.name || 'Unassigned';
      const team = teamMap.get(teamName);
      if (team) {
        team.absenceHours += e.hours;
        if (e.type === TimeOffType.PTO) team.ptoHours += e.hours;
        if (e.type === TimeOffType.SICK) team.sickHours += e.hours;
      }
    });

    return Array.from(teamMap.values())
      .filter((t) => t.total > 0)
      .map((t) => ({
        ...t,
        hoursPerPerson: t.total > 0 ? Math.round(t.absenceHours / t.total) : 0,
      }))
      .sort((a, b) => b.hoursPerPerson - a.hoursPerPerson);
  }, [filteredEmployees, yearEntries]);

  // ── Top Absent Employees ──
  const topAbsent = useMemo(() => {
    const empMap = new Map<string, {
      id: string; name: string; teamName: string; ptoHours: number; sickHours: number; totalHours: number; ptoAllotment: number;
    }>();
    yearEntries.forEach((e) => {
      if (e.type === TimeOffType.WFH) return;
      if (!empMap.has(e.employee._id)) {
        const emp = filteredEmployees.find((em) => em._id === e.employee._id);
        empMap.set(e.employee._id, {
          id: e.employee._id,
          name: e.employee.name,
          teamName: emp?.teamName || emp?.department?.name || e.employee.department?.name || 'Unassigned',
          ptoHours: 0,
          sickHours: 0,
          totalHours: 0,
          ptoAllotment: emp?.ptoAllotment || 0,
        });
      }
      const entry = empMap.get(e.employee._id)!;
      entry.totalHours += e.hours;
      if (e.type === TimeOffType.PTO) entry.ptoHours += e.hours;
      if (e.type === TimeOffType.SICK) entry.sickHours += e.hours;
    });
    return Array.from(empMap.values()).sort((a, b) => b.totalHours - a.totalHours).slice(0, 10);
  }, [yearEntries, filteredEmployees]);

  // ── Allotment Pacing (combined PTO + Sick vs combined allotment) ──
  // Fraction of the selected year already elapsed: someone whose used% is
  // above this is on pace to blow through their combined allotment.
  const yearElapsedFraction =
    yearFilter < new Date().getFullYear()
      ? 1
      : yearFilter > new Date().getFullYear()
        ? 0
        : getDayOfYear(new Date()) / getDaysInYear(new Date());

  const todayYmd = format(new Date(), 'yyyy-MM-dd');
  const yearEndYmd = `${yearFilter}-12-31`;

  const allotmentPacing = useMemo(() => {
    const usage = new Map<string, { ptoUsed: number; sickUsed: number; booked: number }>();
    yearEntries.forEach((e) => {
      if (e.type !== TimeOffType.PTO && e.type !== TimeOffType.SICK) return;
      const u = usage.get(e.employee._id) || { ptoUsed: 0, sickUsed: 0, booked: 0 };
      // Year-straddling bookings (e.g. Dec 28 → Jan 13) only draw on this
      // year's allotment for the business days inside the year — the January
      // tail belongs to next year's pacing, not this year's.
      const withinYearHours = e.endDate > yearEndYmd
        ? e.hours * elapsedBusinessDayFraction(e.startDate, e.endDate, yearEndYmd)
        : e.hours;
      // Only approved hours that have already elapsed count as "used" — a
      // December request entered in July must not inflate today's pace, a
      // pending request may still be rejected, and a request spanning today
      // (e.g. July 1–15 viewed on July 2) only counts its elapsed days.
      // Everything else is "booked".
      const addUsed = (hours: number) => {
        if (e.type === TimeOffType.PTO) u.ptoUsed += hours;
        else u.sickUsed += hours;
      };
      if (e.status !== TimeOffStatus.APPROVED || e.startDate > todayYmd) {
        u.booked += withinYearHours;
      } else if (e.endDate <= todayYmd) {
        addUsed(withinYearHours);
      } else {
        const elapsed = e.hours * elapsedBusinessDayFraction(e.startDate, e.endDate, todayYmd);
        addUsed(elapsed);
        u.booked += withinYearHours - elapsed;
      }
      usage.set(e.employee._id, u);
    });

    return filteredEmployees
      .map((emp) => {
        const u = usage.get(emp._id) || { ptoUsed: 0, sickUsed: 0, booked: 0 };
        const allotment = (emp.ptoAllotment || 0) + (emp.sickTimeAllotment || 0);
        const used = u.ptoUsed + u.sickUsed;
        const committed = used + u.booked;
        const usedPct = allotment > 0 ? (used / allotment) * 100 : 0;
        const committedPct = allotment > 0 ? (committed / allotment) * 100 : 0;
        // Projection: straight-line on hours actually used so far, but never
        // below what is already booked on the calendar for the year.
        const straightLine = yearElapsedFraction > 0 ? used / yearElapsedFraction : used;
        const projected = Math.round(Math.max(straightLine, committed));
        const paceRatio = allotment > 0 ? projected / allotment : 0;
        return {
          id: emp._id,
          name: emp.name,
          teamName: emp.teamName || emp.department?.name || 'Unassigned',
          // Proration can leave fractions; round only the displayed values.
          ptoUsed: Math.round(u.ptoUsed),
          sickUsed: Math.round(u.sickUsed),
          booked: Math.round(u.booked),
          used: Math.round(used),
          committed: Math.round(committed),
          allotment,
          usedPct,
          committedPct,
          projected,
          paceRatio,
          overPace: paceRatio > 1,
          overAllotment: allotment > 0 && committed > allotment,
        };
      })
      .filter((r) => r.allotment > 0)
      .sort((a, b) => b.paceRatio - a.paceRatio);
  }, [filteredEmployees, yearEntries, yearElapsedFraction, todayYmd]);

  // Status buckets are exclusive and match the Status column (see isPacingOnly).
  const pacingOverCount = allotmentPacing.filter(isPacingOnly).length;
  const overAllotmentCount = allotmentPacing.filter((r) => r.overAllotment).length;

  // Client-side search and status chips over the pacing rows; ranks stay
  // the row's position in the full pace ranking.
  const paceQuery = paceSearch.trim();
  const anyPaceFlag = paceFlags.pacingOver || paceFlags.overAllotment;
  const visiblePacing = useMemo(() => {
    const query = paceSearch.trim().toLowerCase();
    const anyFlag = paceFlags.pacingOver || paceFlags.overAllotment;
    return allotmentPacing
      .map((row, index) => ({ row, rank: index + 1 }))
      .filter(({ row }) => !query || row.name.toLowerCase().includes(query) || row.teamName.toLowerCase().includes(query))
      .filter(({ row }) => !anyFlag || (paceFlags.pacingOver && isPacingOnly(row)) || (paceFlags.overAllotment && row.overAllotment));
  }, [allotmentPacing, paceSearch, paceFlags]);
  const clearPaceFilters = () => {
    setPaceSearch('');
    setPaceFlags({ pacingOver: false, overAllotment: false });
  };

  // ── Summary Stats ──
  const totalPtoHours = yearEntries.filter((e) => e.type === TimeOffType.PTO).reduce((s, e) => s + e.hours, 0);
  const totalSickHours = yearEntries.filter((e) => e.type === TimeOffType.SICK).reduce((s, e) => s + e.hours, 0);
  const wfhRequestCount = yearEntries.filter((e) => e.type === TimeOffType.WFH).length;
  const totalWfhDays = yearEntries.filter((e) => e.type === TimeOffType.WFH).reduce((s, e) => s + e.hours, 0) / 8;
  const avgPtoPerPerson = totalCount > 0 ? Math.round(totalPtoHours / totalCount) : 0;
  const avgSickPerPerson = totalCount > 0 ? Math.round(totalSickHours / totalCount) : 0;
  const avgAbsencePerPerson = avgPtoPerPerson + avgSickPerPerson;
  const wfhDaysPerPerson = totalCount > 0 ? Math.round((totalWfhDays / totalCount) * 10) / 10 : 0;

  // Best/worst coverage months
  const pastMonths = monthlyData.filter((m) => m.monthIdx <= today.getMonth());
  const bestMonth = pastMonths.length > 0 ? pastMonths.reduce((a, b) => (a.coveragePct > b.coveragePct ? a : b)) : null;
  const worstMonth = pastMonths.length > 0 ? pastMonths.reduce((a, b) => (a.coveragePct < b.coveragePct ? a : b)) : null;
  // The month in progress is partial: it and every later month draw dashed.
  const currentMonthIndex = today.getMonth();
  const lastCompleteIndex = currentMonthIndex - 1;
  const monthTag = (monthIdx: number) =>
    monthIdx > currentMonthIndex ? 'booked' : monthIdx === currentMonthIndex ? 'to date' : null;

  // ── Scope, period, freshness ──
  const periodLabel = formatDateRange(`${yearFilter}-01-01`, yearEndYmd);
  const fetchedTimes = [employeesFetchedAt, departmentsFetchedAt, entriesFetchedAt].filter(
    (t): t is number => typeof t === 'number',
  );
  // Freshness reports the STALEST source, so a cached list never hides behind a newer one.
  const oldestFetch = fetchedTimes.length > 0 ? Math.min(...fetchedTimes) : null;
  const withUpdated = (text: string) => (oldestFetch !== null ? `${text} · Updated ${formatTime(new Date(oldestFetch))}` : text);

  if (anyLoading) {
    const pending = [
      employeesLoading ? 'employee records' : null,
      departmentsLoading ? 'departments' : null,
      entriesLoading ? 'time-off requests' : null,
    ].filter((source): source is string => source !== null);
    return (
      <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
        {slowLoad && (
          <Alert
            severity="info"
            role="status"
            action={
              // A refetch is ignored while the first request is still in
              // flight, so the honest retry is a fresh page load.
              <Button variant="outlined" size="small" onClick={() => window.location.reload()}>
                Reload page
              </Button>
            }
          >
            Still loading {joinList(pending)}. This can take up to a minute while the server starts up.
          </Alert>
        )}
        <PageLoading label="Loading workforce analytics…" variant="cards" />
      </Box>
    );
  }
  if (employeesError || departmentsError || entriesError) {
    const failed = [
      employeesError ? 'employee records' : null,
      departmentsError ? 'departments' : null,
      entriesError ? 'time-off requests' : null,
    ].filter((source): source is string => source !== null);
    const sources = joinList(failed);
    return (
      <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
        <PageHeader overline="Insights" title="Analytics" meta={[periodLabel]} sx={{ mb: 0 }} />
        <PageError
          title="Couldn’t load analytics"
          description={`${sources.charAt(0).toUpperCase()}${sources.slice(1)} didn’t load, so these figures would be incomplete. Try again in a minute.`}
          onRetry={() => { reloadEmployees(); reloadDepartments(); reloadEntries(); }}
        />
      </Box>
    );
  }

  // Headcounts live in the group switch; the meta line keeps scope, the
  // managers in the selected group, period and freshness.
  const header = (
    <PageHeader
      overline="Insights"
      title="Analytics"
      meta={[
        SEGMENT_SCOPE[segmentFilter],
        totalCount > 0 ? formatCount(managersInView, 'manager') : null,
        periodLabel,
        oldestFetch !== null ? <UpdatedAgo at={oldestFetch} /> : null,
      ]}
      actions={
        employees.length > 0 ? (
          <SegmentedControl<Segment>
            aria-label="Employee group"
            value={segmentFilter}
            onChange={setSegmentFilter}
            options={[
              { value: 'all', label: <>All<Box component="span" sx={{ ml: 0.75, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(employees.length)}</Box></>, 'aria-label': `All employees, ${employees.length}` },
              { value: 'sales', label: <>Sales<Box component="span" sx={{ ml: 0.75, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(salesCount)}</Box></>, 'aria-label': `Sales teams, ${salesCount}` },
              {
                value: 'non-sales',
                label: <>Non-sales<Box component="span" sx={{ ml: 0.75, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(employees.length - salesCount)}</Box></>,
                'aria-label': `Non-sales teams, ${employees.length - salesCount}`,
              },
            ]}
          />
        ) : undefined
      }
      sx={{ mb: 0 }}
    />
  );

  const emptyCardSx = { bgcolor: sbg.bg.surface, border: `1px solid ${sbg.border.default}`, borderRadius: sbg.radius.card };

  // First use: no active employees at all.
  if (employees.length === 0) {
    return (
      <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
        {header}
        <Box sx={emptyCardSx}>
          <EmptyState
            title="No active employees yet"
            body="Coverage, absence and allotment analytics appear here once employees are added and time off is recorded."
            action={
              <Button variant="contained" component={RouterLink} to="/employees">
                Go to employees
              </Button>
            }
          />
        </Box>
      </Box>
    );
  }

  // Filtered-empty: the chosen group has nobody in it.
  if (totalCount === 0) {
    return (
      <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
        {header}
        <Box sx={emptyCardSx}>
          <EmptyState
            title={`No active employees in ${SEGMENT_SCOPE[segmentFilter].toLowerCase()}`}
            body="Sales teams are departments whose names start with “Team” (Team IT counts as non-sales). Switch groups to see the rest of the company."
            action={
              <Button variant="outlined" onClick={() => setSegmentFilter('all')}>
                Show all teams
              </Button>
            }
          />
        </Box>
      </Box>
    );
  }

  // ── KPI comparisons: the selected group vs the rest of the company ──
  const otherGroup = segmentFilter === 'sales' ? 'non-sales teams' : 'sales teams';
  const vsRest = (figure: string) => `vs ${figure} for ${otherGroup}`;
  const deltaVsRest = (inView: number, rest: number | undefined, unit: string, fractionDigits = 0): KpiTileProps['delta'] => {
    if (rest === undefined) return undefined;
    const diff = Number((inView - rest).toFixed(fractionDigits));
    return {
      value: formatDelta(diff, { unit, fractionDigits }),
      // Neutral on purpose: PTO is an earned benefit, and a few hours either
      // way is not a status. The arrow carries the direction.
      tone: 'flat',
      direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat',
    };
  };

  // ── Findings (chart titles state what the data shows) ──
  const absenceRecorded = totalPtoHours + totalSickHours > 0;
  const peakMonth = pastMonths.reduce<(typeof monthlyData)[number] | null>(
    (best, m) => (m.totalHours > 0 && (!best || m.totalHours > best.totalHours) ? m : best),
    null,
  );
  const trendTitle = peakMonth
    ? `Time off peaked in ${peakMonth.monthLong} at ${formatCount(peakMonth.totalHours, 'hour')}`
    : absenceRecorded
      ? `No time off taken yet, ${formatCount(totalPtoHours + totalSickHours, 'hour')} booked ahead`
      : `No PTO or sick time recorded in ${yearFilter} yet`;
  const currentMonthShort = monthlyData[currentMonthIndex]?.month ?? '';
  const dashedLabel = currentMonthIndex < 11 ? `${currentMonthShort} to date and booked ahead` : `${currentMonthShort} to date`;

  const coverageTitle = worstMonth
    ? worstMonth.coveragePct >= 100
      ? 'Full coverage every month so far'
      : `Coverage dipped to ${formatPercent(worstMonth.coveragePct)} in ${worstMonth.monthLong}`
    : 'Coverage by month';
  const coverageLevel = (m: (typeof monthlyData)[number]) =>
    m.monthIdx > currentMonthIndex
      ? null
      : m.coveragePct < COVERAGE_DANGER
        ? 'very-low'
        : m.coveragePct < COVERAGE_WARNING
          ? 'low'
          : null;
  const hasLowMonth = monthlyData.some((m) => coverageLevel(m) === 'low');
  const hasVeryLowMonth = monthlyData.some((m) => coverageLevel(m) === 'very-low');
  const hasBookedMonth = currentMonthIndex < 11;

  // The headline names a team big enough for "per person" to mean something;
  // a one-person team is one individual's time off.
  const topTeam = teamCoverage[0];
  const leadTeam = teamCoverage.find((t) => t.total >= MIN_TEAM_SIZE && t.hoursPerPerson > 0);
  const teamsTitle =
    !topTeam || topTeam.hoursPerPerson <= 0
      ? 'No team time off recorded yet'
      : !leadTeam
        ? `${topTeam.name} took the most time off per person`
        : leadTeam === topTeam
          ? `${leadTeam.name} took the most time off per person`
          : `${leadTeam.name} took the most time off per person among teams of ${MIN_TEAM_SIZE} or more`;

  // "Above average" only when it singles out a minority well over the average.
  const aboveLine = avgAbsencePerPerson * ABOVE_AVERAGE_MARGIN;
  const wellAboveCount = avgAbsencePerPerson > 0 ? teamCoverage.filter((t) => t.hoursPerPerson > aboveLine).length : 0;
  const markAboveAverage = wellAboveCount > 0 && wellAboveCount * 2 < teamCoverage.length;

  const busiestDay = dowData.reduce((best, d) => (d.count > best.count ? d : best), dowData[0]);
  const weekdayRecorded = dowData.some((d) => d.count > 0);
  const weekdayTitle = weekdayRecorded
    ? `${DAY_NAMES_LONG[busiestDay.name]}s have the most PTO, sick and WFH days`
    : 'No requests by weekday yet';

  const paceTitle =
    allotmentPacing.length === 0
      ? 'Allotment pacing'
      : pacingOverCount + overAllotmentCount > 0
        ? `${formatCount(pacingOverCount + overAllotmentCount, 'person', 'people')} on pace to exceed their allotment`
        : 'Everyone is on pace for their allotment';
  const elapsedPct = Math.min(yearElapsedFraction * 100, 100);

  const statusWords = [paceFlags.pacingOver ? 'pacing over' : null, paceFlags.overAllotment ? 'over allotment' : null]
    .filter((word): word is string => word !== null)
    .join(' or ');
  const filteredPaceBody =
    paceQuery && anyPaceFlag
      ? `Nobody named or on a team matching “${paceQuery}” is ${statusWords}.`
      : paceQuery
        ? `Nobody named or on a team matching “${paceQuery}”.`
        : `Nobody in this group is ${statusWords}.`;

  const teamItems: BarListItem[] = teamCoverage.slice(0, TEAM_ROWS).map((team) => {
    const above = markAboveAverage && team.hoursPerPerson > aboveLine;
    return {
      key: team.name,
      label: team.name,
      caption: `${formatCount(team.total, 'person', 'people')}${
        above ? ` · ${formatPercent(Math.round((team.hoursPerPerson / avgAbsencePerPerson - 1) * 100))} above average` : ''
      }`,
      value: team.hoursPerPerson,
      display: hoursLabel(team.hoursPerPerson),
      tooltip: `${team.name}: ${hoursLabel(team.ptoHours)} PTO and ${hoursLabel(team.sickHours)} sick across ${formatCount(team.total, 'person', 'people')}`,
    };
  });

  const weekdayItems: BarListItem[] = dowData.map((d) => ({
    key: d.name,
    label: DAY_NAMES_LONG[d.name],
    value: d.count,
    display: formatCount(d.count, 'day'),
  }));

  const monthCell = (m: (typeof monthlyData)[number]) => {
    const tag = monthTag(m.monthIdx);
    return tag ? `${m.month} (${tag})` : m.month;
  };

  const quietState = (title: string, body: string) => <EmptyState compact title={title} body={body} />;

  const trendCard = (
    <ChartCard
      title={trendTitle}
      period={`${periodLabel} · PTO and sick hours by month`}
      source={withUpdated('Source: approved and pending time-off requests')}
      legend={
        absenceRecorded ? (
          <>
            <LegendItem color={PTO_COLOR} label="PTO" />
            <LegendItem color={SICK_COLOR} label="Sick" />
            <DashedLegendItem label={dashedLabel} />
          </>
        ) : undefined
      }
      table={
        absenceRecorded
          ? {
              columns: ['Month', 'PTO (h)', 'Sick (h)', 'Total (h)', 'People out', 'Coverage'],
              numericColumns: [1, 2, 3, 4, 5],
              rows: monthlyData.map((m) => [
                monthCell(m),
                formatNumber(m.ptoHours),
                formatNumber(m.sickHours),
                formatNumber(m.totalHours),
                formatNumber(m.uniqueOut),
                formatPercent(m.coveragePct),
              ]),
            }
          : undefined
      }
    >
      {absenceRecorded ? (
        <MonthlyLineChart
          aria-label={`PTO and sick hours by month, ${yearFilter}. ${trendTitle}. Use the arrow keys to step through months, or the Table view for every value.`}
          labels={monthlyData.map((m) => m.month)}
          lastCompleteIndex={lastCompleteIndex}
          focusIndex={currentMonthIndex}
          series={[
            { key: 'pto', label: 'PTO', color: PTO_COLOR, values: monthlyData.map((m) => m.ptoHours) },
            { key: 'sick', label: 'Sick', color: SICK_COLOR, values: monthlyData.map((m) => m.sickHours) },
          ]}
          pointAt={(index) => {
            const m = monthlyData[index];
            const tag = monthTag(m.monthIdx);
            return {
              title: `${m.monthLong} ${yearFilter}${tag === 'booked' ? ' · booked ahead' : tag === 'to date' ? ' · to date' : ''}`,
              rows: [
                { key: 'pto', label: 'PTO', value: hoursLabel(m.ptoHours), color: PTO_COLOR },
                { key: 'sick', label: 'Sick', value: hoursLabel(m.sickHours), color: SICK_COLOR },
                { key: 'total', label: 'Total', value: hoursLabel(m.totalHours) },
                { key: 'out', label: 'People out', value: formatNumber(m.uniqueOut) },
                { key: 'coverage', label: 'Coverage', value: formatPercent(m.coveragePct) },
              ],
            };
          }}
        />
      ) : (
        quietState(
          `No PTO or sick time recorded in ${yearFilter} yet`,
          'Monthly hours appear here as approved and pending requests come in.',
        )
      )}
    </ChartCard>
  );

  const coverageCard = (
    <ChartCard
      title={coverageTitle}
      period={`${periodLabel} · weekday hours available after PTO and sick time`}
      source={withUpdated('Source: approved and pending time-off requests and active headcount, 8 h per weekday')}
      legend={
        hasLowMonth || hasVeryLowMonth || hasBookedMonth ? (
          <>
            {hasLowMonth && (
              <KeyItem
                mark={<Box component="span" sx={{ display: 'inline-flex', color: sbg.status.warning.icon }}><TriangleAlert size={14} /></Box>}
                label={`Low: under ${COVERAGE_WARNING}%`}
              />
            )}
            {hasVeryLowMonth && (
              <KeyItem
                mark={<Box component="span" sx={{ display: 'inline-flex', color: sbg.status.danger.icon }}><CircleAlert size={14} /></Box>}
                label={`Very low: under ${COVERAGE_DANGER}%`}
              />
            )}
            {hasBookedMonth && (
              <KeyItem
                mark={<Box component="span" sx={{ width: 14, height: 10, borderRadius: '3px', border: `1px dashed ${sbg.border.strong}` }} />}
                label="Booked ahead"
              />
            )}
          </>
        ) : undefined
      }
      table={{
        columns: ['Month', 'Coverage', 'People out', 'Absence (h)'],
        numericColumns: [1, 2, 3],
        rows: monthlyData.map((m) => [
          monthCell(m),
          formatPercent(m.coveragePct),
          formatNumber(m.uniqueOut),
          formatNumber(m.totalHours),
        ]),
      }}
    >
      <Box sx={{ display: 'grid', gap: 2.5 }}>
        {bestMonth && worstMonth && (
          <SpecGrid
            minColumnWidth={120}
            items={[
              {
                label: 'Highest',
                value: formatPercent(bestMonth.coveragePct),
                caption: `${bestMonth.monthLong} · ${formatCount(bestMonth.uniqueOut, 'person', 'people')} out`,
              },
              {
                label: 'Lowest',
                value: formatPercent(worstMonth.coveragePct),
                caption: `${worstMonth.monthLong} · ${formatCount(worstMonth.uniqueOut, 'person', 'people')} out`,
              },
            ]}
          />
        )}
        <Box
          component="ul"
          aria-label="Coverage by month"
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'grid',
            gap: 1,
            gridTemplateColumns: { xs: 'repeat(4, minmax(0, 1fr))', md: 'repeat(6, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
          }}
        >
          {monthlyData.map((m) => {
            const booked = m.monthIdx > currentMonthIndex;
            const level = coverageLevel(m);
            const tone = level === 'very-low' ? sbg.status.danger : level === 'low' ? sbg.status.warning : null;
            const LevelIcon = level === 'very-low' ? CircleAlert : TriangleAlert;
            return (
              <Box
                component="li"
                key={m.month}
                sx={{
                  display: 'grid',
                  gap: 0.25,
                  px: 1,
                  py: 0.75,
                  minWidth: 0,
                  borderRadius: sbg.radius.md,
                  border: `1px ${booked ? 'dashed' : 'solid'} ${tone ? tone.border : sbg.border.subtle}`,
                  bgcolor: tone ? tone.bg : 'transparent',
                }}
              >
                <Typography variant="caption" component="span" sx={{ color: tone ? tone.text : sbg.text.secondary }}>
                  {m.month}
                  {booked && <span className="sbg-sr-only">, booked ahead</span>}
                  {m.monthIdx === currentMonthIndex && <span className="sbg-sr-only">, to date</span>}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: tone ? tone.text : booked ? sbg.text.tertiary : sbg.text.primary }}>
                  {tone && (
                    <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', color: tone.icon }}>
                      <LevelIcon size={12} />
                    </Box>
                  )}
                  <Typography variant="labelMd" component="span" sx={{ color: 'inherit', fontVariantNumeric: 'tabular-nums' }}>
                    {formatPercent(m.coveragePct)}
                  </Typography>
                </Box>
                <Typography variant="caption" component="span" noWrap sx={{ color: tone ? tone.text : sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                  {level ? (
                    <>
                      {level === 'very-low' ? 'Very low' : 'Low'}
                      <span className="sbg-sr-only">
                        {`, below ${level === 'very-low' ? COVERAGE_DANGER : COVERAGE_WARNING}% coverage, ${formatCount(m.uniqueOut, 'person', 'people')} out`}
                      </span>
                    </>
                  ) : (
                    `${formatNumber(m.uniqueOut)} out`
                  )}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </ChartCard>
  );

  const teamsCard = (
    <ChartCard
      title={teamsTitle}
      period={`${periodLabel} · PTO and sick hours per person · ${hoursLabel(avgAbsencePerPerson)} average`}
      source={withUpdated('Source: approved and pending time-off requests and team headcount')}
      table={
        teamCoverage.length > 0
          ? {
              columns: ['Team', 'People', 'PTO (h)', 'Sick (h)', 'Per person (h)'],
              numericColumns: [1, 2, 3, 4],
              rows: teamCoverage.map((team) => [
                team.name,
                formatNumber(team.total),
                formatNumber(team.ptoHours),
                formatNumber(team.sickHours),
                formatNumber(team.hoursPerPerson),
              ]),
            }
          : undefined
      }
    >
      {teamCoverage.length > 0 && topTeam && topTeam.hoursPerPerson > 0 ? (
        <>
          <BarList items={teamItems} />
          {teamCoverage.length > teamItems.length && (
            <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 1.5 }}>
              Showing the top {teamItems.length} of {formatCount(teamCoverage.length, 'team')}. The Table view lists every team.
            </Typography>
          )}
        </>
      ) : (
        quietState('No team time off recorded yet', 'Teams appear here, ranked by hours per person, once PTO or sick time is requested.')
      )}
    </ChartCard>
  );

  const weekdayCard = (
    <ChartCard
      title={weekdayTitle}
      period={`${periodLabel} · weekdays inside a PTO, sick or WFH request`}
      source={withUpdated('Source: approved and pending time-off requests')}
      table={
        weekdayRecorded
          ? {
              columns: ['Weekday', 'Days'],
              numericColumns: [1],
              rows: dowData.map((d) => [DAY_NAMES_LONG[d.name], formatNumber(d.count)]),
            }
          : undefined
      }
    >
      {weekdayRecorded ? (
        <BarList items={weekdayItems} barHeight={10} />
      ) : (
        quietState('No requests by weekday yet', 'The weekday pattern appears once PTO, sick or WFH requests are entered.')
      )}
    </ChartCard>
  );

  const columnSx = { display: 'grid', gap: 3, alignContent: 'start', minWidth: 0 };

  return (
    <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
      {header}

      {/* ── KPI row ── */}
      <KpiGrid sx={{ mb: 0 }}>
        <KpiTile
          featured
          label="Time off per person"
          value={formatCount(avgAbsencePerPerson, 'hour')}
          delta={deltaVsRest(avgAbsencePerPerson, restPerPerson ? restPerPerson.pto + restPerPerson.sick : undefined, ' h')}
          caption={
            restPerPerson
              ? `${vsRest(hoursLabel(restPerPerson.pto + restPerPerson.sick))}, ${yearFilter}`
              : `PTO and sick, ${yearFilter}`
          }
          info={`Approved and pending PTO and sick hours on requests starting in ${yearFilter}, including dates still ahead, divided by active employees in view. Rejected requests and WFH are excluded. With Sales or Non-sales selected, the comparison is the other group.`}
        />
        <KpiTile
          label="PTO per person"
          value={formatCount(avgPtoPerPerson, 'hour')}
          delta={deltaVsRest(avgPtoPerPerson, restPerPerson?.pto, ' h')}
          caption={
            restPerPerson
              ? `${vsRest(hoursLabel(restPerPerson.pto))} · ${hoursLabel(totalPtoHours)} total, ${yearFilter}`
              : `${hoursLabel(totalPtoHours)} total, ${yearFilter}`
          }
        />
        <KpiTile
          label="Sick time per person"
          value={formatCount(avgSickPerPerson, 'hour')}
          delta={deltaVsRest(avgSickPerPerson, restPerPerson?.sick, ' h')}
          caption={
            restPerPerson
              ? `${vsRest(hoursLabel(restPerPerson.sick))} · ${hoursLabel(totalSickHours)} total, ${yearFilter}`
              : `${hoursLabel(totalSickHours)} total, ${yearFilter}`
          }
        />
        <KpiTile
          label="WFH per person"
          value={formatDays(wfhDaysPerPerson)}
          delta={deltaVsRest(wfhDaysPerPerson, restPerPerson?.wfhDays, ' days', 1)}
          caption={
            restPerPerson
              ? `${vsRest(formatDays(restPerPerson.wfhDays))} · ${formatCount(Math.round(totalWfhDays), 'day')} total, ${yearFilter}`
              : `${formatCount(Math.round(totalWfhDays), 'day')} on ${formatCount(wfhRequestCount, 'request')}, ${yearFilter}`
          }
          info={`Approved and pending work-from-home days (8 h each) on requests starting in ${yearFilter}, divided by active employees in view.`}
        />
      </KpiGrid>

      {/* ── Charts: two columns that stack independently, so no card is
          stretched to its neighbour's height ── */}
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          alignItems: 'start',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 2fr) minmax(0, 1fr)' },
        }}
      >
        <Box sx={columnSx}>
          {trendCard}
          {teamsCard}
        </Box>
        <Box sx={columnSx}>
          {coverageCard}
          {weekdayCard}
        </Box>
      </Box>

      {/* ── Most time off ── */}
      <Box component="section" aria-labelledby="analytics-most-time-off" sx={{ mt: 1, minWidth: 0 }}>
        <SectionHeading
          id="analytics-most-time-off"
          title="Most time off this year"
          description={`Top 10 by PTO and sick hours in ${yearFilter}, used and booked: approved and pending requests, including dates still ahead.`}
        />
        <TableCard aria-label="Employees with the most time off">
          <Table size="small" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell align="right" sx={{ width: 48 }}>Rank</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell align="right">PTO</TableCell>
                <TableCell align="right">Sick</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell sx={{ width: 340 }}>PTO used and booked vs allotment</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topAbsent.length === 0 ? (
                <TableEmptyRow
                  colSpan={6}
                  title={`No PTO or sick time recorded in ${yearFilter} yet`}
                  body="The ten people with the most hours appear here as requests come in."
                />
              ) : (
                topAbsent.map((emp, idx) => {
                  const usedPct = emp.ptoAllotment > 0 ? pct(emp.ptoHours, emp.ptoAllotment) : 0;
                  const over = usedPct > 100;
                  return (
                    <TableRow key={emp.id}>
                      <TableCell align="right" sx={{ color: sbg.text.secondary }}>{idx + 1}</TableCell>
                      <TableCell>
                        <PersonCell name={emp.name} secondary={emp.teamName} size={24} />
                      </TableCell>
                      <TableCell align="right">{hoursLabel(emp.ptoHours)}</TableCell>
                      <TableCell align="right">{hoursLabel(emp.sickHours)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="labelMd" component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {hoursLabel(emp.totalHours)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {emp.ptoAllotment > 0 ? (
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(80px, 1fr) 48px 148px', alignItems: 'center', gap: 1.5 }}>
                            {/* Brand fill: the state is the word beside it, not the bar's color. */}
                            <Meter
                              value={Math.min(usedPct, 100)}
                              max={100}
                              tone="brand"
                              aria-label={`${emp.name}: ${formatPercent(usedPct)} of ${hoursLabel(emp.ptoAllotment)} PTO allotment, used and booked`}
                            />
                            <Typography variant="body2" component="span" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: sbg.text.primary }}>
                              {formatPercent(usedPct)}
                            </Typography>
                            <Box sx={{ minWidth: 0 }}>{over && <StatusPill tone="danger">Over PTO allotment</StatusPill>}</Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
                            {MISSING}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableCard>
      </Box>

      {/* ── Allotment pacing (all employees) ── */}
      <Box component="section" aria-labelledby="analytics-pacing" sx={{ mt: 1, minWidth: 0 }}>
        <SectionHeading
          id="analytics-pacing"
          title={paceTitle}
          description={`Combined PTO and sick allotment. Used is approved time already taken; booked is pending or still ahead. ${formatPercent(yearElapsedFraction * 100)} of ${yearFilter} has passed, so a solid bar past the marker is pacing over. Over allotment means used plus booked is more than the allotment.`}
        />
        <TableCard
          aria-label="Allotment pacing by employee"
          maxHeight={560}
          toolbar={
            allotmentPacing.length > 0 ? (
              <Toolbar
                end={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <LegendItem color={sbg.action.primary} label="Used" />
                    <LegendItem color={sbg.action.primary} label="Booked or pending" hatched />
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                      <Box aria-hidden="true" sx={{ width: 2, height: 12, borderRadius: '1px', bgcolor: sbg.text.primary }} />
                      <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
                        Year elapsed
                      </Typography>
                    </Box>
                  </Box>
                }
              >
                <SearchField value={paceSearch} onChange={setPaceSearch} label="Search employees or teams" width={260} />
                <FilterChip
                  selected={paceFlags.pacingOver}
                  onClick={() => setPaceFlags((flags) => ({ ...flags, pacingOver: !flags.pacingOver }))}
                  count={pacingOverCount}
                >
                  Pacing over
                </FilterChip>
                <FilterChip
                  selected={paceFlags.overAllotment}
                  onClick={() => setPaceFlags((flags) => ({ ...flags, overAllotment: !flags.overAllotment }))}
                  count={overAllotmentCount}
                >
                  Over allotment
                </FilterChip>
              </Toolbar>
            ) : undefined
          }
        >
          <Table size="small" stickyHeader sx={{ minWidth: 960 }}>
            <TableHead>
              <TableRow>
                <TableCell align="right" sx={{ width: 48 }}>Rank</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell align="right">PTO used</TableCell>
                <TableCell align="right">Sick used</TableCell>
                <TableCell align="right">Booked</TableCell>
                <TableCell sx={{ minWidth: 240 }}>Used of allotment</TableCell>
                <TableCell align="right">Projected by Dec 31</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allotmentPacing.length === 0 ? (
                <TableEmptyRow
                  colSpan={8}
                  title="No employees with PTO or sick allotments in this group"
                  body="Set PTO and sick allotments on each employee record to track their pace here."
                />
              ) : visiblePacing.length === 0 ? (
                <TableEmptyRow
                  colSpan={8}
                  title="No employees match these filters"
                  body={filteredPaceBody}
                  action={
                    <Button variant="outlined" size="small" onClick={clearPaceFilters}>
                      Clear filters
                    </Button>
                  }
                />
              ) : (
                visiblePacing.map(({ row, rank }) => {
                  const tone = row.overAllotment ? 'danger' : row.overPace ? 'warning' : 'brand';
                  return (
                    <TableRow key={row.id}>
                      <TableCell align="right" sx={{ color: sbg.text.secondary }}>{rank}</TableCell>
                      <TableCell>
                        <PersonCell name={row.name} secondary={row.teamName} size={24} />
                      </TableCell>
                      <TableCell align="right">{hoursLabel(row.ptoUsed)}</TableCell>
                      <TableCell align="right">{hoursLabel(row.sickUsed)}</TableCell>
                      <TableCell align="right" sx={{ color: row.booked > 0 ? sbg.text.primary : sbg.text.secondary }}>
                        {row.booked > 0 ? `+${hoursLabel(row.booked)}` : hoursLabel(0)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) auto', alignItems: 'center', gap: 1.5 }}>
                          <PaceBar
                            usedPct={row.usedPct}
                            committedPct={row.committedPct}
                            elapsedPct={elapsedPct}
                            tone={tone}
                            label={`${row.name}: ${hoursLabel(row.used)} used and ${hoursLabel(row.committed)} including booked, of ${hoursLabel(row.allotment)}. ${formatPercent(elapsedPct)} of the year has passed.`}
                          />
                          <Typography variant="body2" component="span" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: sbg.text.primary }}>
                            {formatNumber(row.used)} of {hoursLabel(row.allotment)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {hoursLabel(row.projected)}
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                          {formatPercent(row.paceRatio, { ratio: true })} of allotment
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.overAllotment ? (
                          <StatusPill tone="danger">Over allotment</StatusPill>
                        ) : row.overPace ? (
                          <StatusPill tone="warning">Pacing over</StatusPill>
                        ) : (
                          <StatusPill tone="neutral">On pace</StatusPill>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableCard>
      </Box>
    </Box>
  );
};

export default Analytics;
